import { plainToClass } from "class-transformer";
import { LMSSiteStudyOverviewDto, LMSSiteStudyUserDto, studyIdParam } from "./trainings.objects";
import { IFilter } from "../../../../shared/scripts/filtering/filtering.entities";
import { InitializeFilterDropdown, GetSelectedDropdownValues } from "../../../../shared/scripts/filtering/filtering.utilities";

export class LMSSettingsPage implements IFilter {
    sitesTable: any;
    usersTable: any;
    allSites: LMSSiteStudyOverviewDto[];
    sites: LMSSiteStudyOverviewDto[];
    mySiteIds: number[];
    showOnlyMySites: boolean = false;
    isShowingUsers: boolean = false;
    private usersLoaded: boolean = false;

    constructor(allSitesJson: any, mySiteIds: number[], startOnUsersTab: boolean = false) {
        this.allSites = plainToClass(LMSSiteStudyOverviewDto, <LMSSiteStudyOverviewDto[]>allSitesJson);
        this.mySiteIds = mySiteIds ?? [];
        this.sites = this.allSites;

        this.initializeFilters();
        this.initializeSearch();
        this.initializeTables();
        this.initializeTabs();

        if (startOnUsersTab) {
            // Razor already rendered the Users tab as active; this syncs our state and runs
            // the lazy load + column measuring. Let the method flip the flag itself - its
            // early-return guard would no-op if we pre-set isShowingUsers.
            this.showUsersTab();
        }

        $('#my-sites-toggle').on('change', (e) => {
            this.showOnlyMySites = (e.target as HTMLInputElement).checked;
            this.triggerFilter();
        });
    }

    private initializeFilters(): void {
        InitializeFilterDropdown("#study-affiliation", [], this);
    }

    private initializeSearch(): void {
        $('#lms-site-search').on('keyup', (e) => {
            this.activeTable().search((e.target as HTMLInputElement).value).draw();
        });

        $('#lms-training-search-clear').on('click', () => {
            $('#lms-site-search').val('');
            this.activeTable().search('').draw();
        });
    }

    // The search box is shared by both tabs, so it always drives whichever table is visible
    private activeTable(): any {
        return this.isShowingUsers ? this.usersTable : this.sitesTable;
    }

    private initializeTables(): void {
        this.sitesTable = $('#lmsSitesTable').DataTable({
            dom: 'rtip',
            autoWidth: false,
            info: false,
            paging: false,
            search: true,
            scrollX: true,
            scrollY: '60vh',
            scrollCollapse: true,
            orderCellsTop: true,
            order: [[0, 'asc']], // order alphabetically by title
            columns: [
                { data: "siteName", title: "Site", className: "site-name text-nowrap" },
                { data: "studyName", title: "Study" },
                { data: "overdue", title: "Overdue" },
                { data: "incomplete", title: "Incomplete" },
                { data: "expired", title: "Expired" },
                { data: "complete", title: "Complete" },
                {
                    data: null,
                    title: "",
                    className: "text-center lms-table-col-actions",
                    orderable: false,
                    render: function () {
                        return `<button class="lms-export-trainings"><i class="fa-solid fa-download"></i></button>`;
                    }
                }
            ],
            data: this.sites
        });

        $('#lmsSitesTable tbody').on('click', 'tr', (e) => {
            const rowData: LMSSiteStudyOverviewDto = this.sitesTable.row(e.currentTarget).data();
            if (!rowData) return;

            window.location.href = `/secure/lms/settings/details?siteId=${rowData.siteId}${studyIdParam(rowData.studyId)}`;
        });

        $('#lmsSitesTable tbody').on('click', '.lms-export-trainings', (e) => {
            e.stopPropagation();

            const rowData: LMSSiteStudyOverviewDto = this.sitesTable.row($(e.currentTarget).closest('tr')).data();
            if (!rowData) return;

            const siteName = encodeURIComponent(rowData.siteName);
            const studyName = encodeURIComponent(rowData.studyName ?? '');
            window.location.href = `/secure/lms/settings/settings?handler=ExportUserTrainingsCsv&siteId=${rowData.siteId}${studyIdParam(rowData.studyId)}&siteName=${siteName}&studyName=${studyName}`;
        })

        // Portal-wide user counts: no site/study scope, so the drill-down takes only a userId
        this.usersTable = $('#lmsAllUsersTable').DataTable({
            dom: 'rtip',
            autoWidth: false,
            info: false,
            paging: false,
            search: true,
            scrollX: true,
            scrollY: '60vh',
            scrollCollapse: true,
            order: [[0, 'asc']],
            columns: [
                { data: "userName", title: "User", className: "user-name text-nowrap" },
                { data: "roles", title: "Role" },
                { data: "overdue", title: "Overdue" },
                { data: "incomplete", title: "Incomplete" },
                { data: "expired", title: "Expired" },
                { data: "complete", title: "Complete" },
                { data: "total", title: "Total" },
            ],
            data: []
        });

        $('#lmsAllUsersTable tbody').on('click', 'tr', (e) => {
            const rowData: LMSSiteStudyUserDto = this.usersTable.row(e.currentTarget).data();
            if (!rowData) return;

            window.location.href = `/secure/lms/settings/details/user?userId=${rowData.userId}`;
        });
    }

    private initializeTabs(): void {
        $('#toggleSitesBtn').on('click', () => this.showSitesTab());
        $('#toggleUsersBtn').on('click', () => this.showUsersTab());
    }

    private showSitesTab(): void {
        if (!this.isShowingUsers) return;

        this.isShowingUsers = false;
        $('#toggleUsersBtn').removeClass('active');
        $('#toggleSitesBtn').addClass('active');

        $('#lmsAllUsersTableContainer').addClass('d-none');
        $('#lmsSitesTableContainer').removeClass('d-none');

        $('#my-sites-filter').removeClass('d-none');
        $('#study-affiliation-filter').removeClass('d-none');

        this.syncSearchBox();
        this.syncUrl();
        // DataTables measures column widths at construction, so a table built (or left)
        // inside a hidden container needs re-measuring once it is actually visible
        this.sitesTable.columns.adjust();
    }

    private showUsersTab(): void {
        if (this.isShowingUsers) return;

        this.isShowingUsers = true;
        $('#toggleSitesBtn').removeClass('active');
        $('#toggleUsersBtn').addClass('active');

        $('#lmsSitesTableContainer').addClass('d-none');
        $('#lmsAllUsersTableContainer').removeClass('d-none');

        // Both filters key off siteId/studyId, which the portal-wide user rows do not have
        $('#my-sites-filter').addClass('d-none');
        $('#study-affiliation-filter').addClass('d-none');

        this.loadUsers();
        this.syncSearchBox();
        this.syncUrl();
        this.usersTable.columns.adjust();
    }

    // Each table keeps its own search term; show the active one's in the shared box
    private syncSearchBox(): void {
        $('#lms-site-search').val(this.activeTable().search());
    }

    // Keep ?tab= in the address bar so a refresh, a bookmark, and the browser Back button
    // all land on the same tab the drill-down breadcrumb points at
    private syncUrl(): void {
        const url = this.isShowingUsers ? '?tab=users' : window.location.pathname;
        history.replaceState(null, '', url);
    }

    private loadUsers(): void {
        if (this.usersLoaded) return;
        this.usersLoaded = true;

        $.get('?handler=Users')
            .done((result: LMSSiteStudyUserDto[]) => {
                const rows = plainToClass(LMSSiteStudyUserDto, result ?? []);
                this.usersTable.clear();
                this.usersTable.rows.add(rows).draw();
                // rows can land after the tab switch already adjusted an empty table
                this.usersTable.columns.adjust();
            });
    }

    public triggerFilter(): void {
        if (this.isShowingUsers) return; // both filters apply to the Sites table only

        const selectedStudyIds = GetSelectedDropdownValues('#study-affiliation');

        this.sites = this.allSites.filter(s =>
            s.matchesFilter(selectedStudyIds) && (!this.showOnlyMySites || this.mySiteIds.includes(s.siteId))
        );
        this.updateTable(this.sites);
    }

    private updateTable(matchingItems: LMSSiteStudyOverviewDto[]): void {
        this.sitesTable.clear();
        this.sitesTable.rows.add(matchingItems).draw();
    }
}
